import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { getAvailability, createBooking, getConfig } from "@/lib/api";
import { formatDateLong, formatDateShort, parseISODate } from "@/lib/format";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Users, Mail, Phone, School, User, ArrowRight, Check, Copy, FileText, Download, MapPin, CreditCard, Bell } from "lucide-react";
import WaitlistModal from "@/components/WaitlistModal";

const SESSIONS_LABEL = ["09:30", "10:45", "12:00", "13:15"];

const INFO_PACK_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_9ec7afdd-8040-4034-8218-64e5a74fdccb/artifacts/lmjwb4zy_COI%20-%20School%20Information%20Pack%20-%20Clonmel.pdf";
const LOGO_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_9ec7afdd-8040-4034-8218-64e5a74fdccb/artifacts/8npbah8c_COI%20-%20Small%20Logo.png";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatRangeShort = (r) => {
  const s = parseISODate(r.start);
  const e = parseISODate(r.end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) return `${s.getDate()}–${e.getDate()} ${MONTHS[e.getMonth()]}`;
  return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]}`;
};

const rangesLabel = (ranges) => (ranges || []).map(formatRangeShort).join(" · ");

export default function BookingPage() {
  const [availability, setAvailability] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [waitlistSlot, setWaitlistSlot] = useState(null); // {date, time} or null

  const [form, setForm] = useState({
    school_name: "",
    school_address: "",
    teacher_name: "",
    email: "",
    phone: "",
    county: "",
    payment_method: "",
    group_size: "",
    age_group: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [data, cfg] = await Promise.all([getAvailability(), getConfig()]);
      setAvailability(data);
      setConfig(cfg);
    } catch (e) {
      toast.error("Failed to load availability");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSelectSlot = (dateISO, time, booked, blocked) => {
    if (blocked) return; // silently ignore, no waitlist for closed-off slots
    if (booked) {
      setWaitlistSlot({ date: dateISO, time });
      return;
    }
    setSelectedDate(dateISO);
    setSelectedTime(time);
    setTimeout(() => {
      document.getElementById("booking-form-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time slot first");
      return;
    }
    const groupSize = parseInt(form.group_size, 10);
    if (!groupSize || groupSize <= 0) {
      toast.error("Group size must be a positive number");
      return;
    }
    if (groupSize > 75) {
      toast.error("Maximum group size is 75");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        date: selectedDate,
        time: selectedTime,
        school_name: form.school_name,
        school_address: form.school_address,
        teacher_name: form.teacher_name,
        email: form.email,
        phone: form.phone,
        county: form.county,
        payment_method: form.payment_method,
        group_size: groupSize,
        age_group: form.age_group,
        notes: form.notes,
      };
      const res = await createBooking(payload);
      setConfirmation(res);
      toast.success("Booking submitted!");
      setForm({
        school_name: "",
        school_address: "",
        teacher_name: "",
        email: "",
        phone: "",
        county: "",
        payment_method: "",
        group_size: "",
        age_group: "",
        notes: "",
      });
      setSelectedDate(null);
      setSelectedTime(null);
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed";
      toast.error(typeof msg === "string" ? msg : "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = () => {
    if (confirmation?.reference) {
      navigator.clipboard.writeText(confirmation.reference);
      toast.success("Reference copied");
    }
  };

  // Hide days from public view only when EVERY slot has been admin-blocked
  // (either whole-day block or all individual slots blocked). Days that fill up
  // via genuine bookings still appear as "Fully booked".
  const visibleAvailability = availability.filter((d) => !d.slots.every((s) => s.blocked));

  const totalSlots = visibleAvailability.length * SESSIONS_LABEL.length;
  const bookedCount = visibleAvailability.reduce((n, d) => n + d.slots.filter((s) => s.booked).length, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* HERO */}
      <section className="border-b-2 border-slate-900 bg-white relative overflow-hidden grid-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 border-2 border-slate-900 bg-sky-500 text-white px-3 py-1 font-mono-tight text-[11px] uppercase tracking-[0.25em] mb-6" data-testid="hero-ranges-badge">
              <img src={LOGO_URL} alt="" className="w-4 h-4 object-contain rounded-full bg-white" /> {config ? rangesLabel(config.booking_ranges) : "30 Nov – 22 Dec"}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] uppercase text-slate-900">
              Clonmel Ice Rink <span className="bg-sky-500 text-white px-2 -skew-x-3 inline-block">Session</span>
              <br />School Bookings
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-slate-700 leading-relaxed">
              Teachers, reserve your{" "}
              <span className="font-mono-tight font-bold text-slate-900">45-minute</span> skate session for your school group.
              Weekdays only, first come first served.{" "}
              <span className="font-mono-tight font-bold text-slate-900">€14 per child</span> — teachers &amp; SNAs skate{" "}
              <span className="bg-sky-500 text-white px-1.5 font-bold">FREE</span>.
              No deposit or pre-payments required.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#slot-grid"
                className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 flex items-center gap-2"
                data-testid="cta-book-now"
              >
                Book a slot <ArrowRight className="w-4 h-4" strokeWidth={3} />
              </a>
              <a
                href="/lookup"
                className="bg-white text-slate-900 font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3"
                data-testid="cta-lookup"
              >
                Find my booking
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border-2 border-slate-900 bg-white brutal-shadow-lg overflow-hidden">
              <div className="aspect-[4/3] bg-slate-900 relative overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_group-ice-sessions/artifacts/u5ccyisd_image.png"
                  alt="Clonmel On Ice rink"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 diagonal-stripes pointer-events-none" />
              </div>
              <div className="border-t-2 border-slate-900 p-5 grid grid-cols-3 gap-4">
                <StatBlock label="Days" value={visibleAvailability.length || "—"} />
                <StatBlock label="Slots/day" value="4" />
                <StatBlock label="Booked" value={loading ? "—" : `${bookedCount}/${totalSlots}`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INFO PACK */}
      <section className="border-b-2 border-slate-900 bg-sky-500 text-white" data-testid="info-pack-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-2xl">
            <div className="w-12 h-12 bg-white border-2 border-slate-900 flex-shrink-0 flex items-center justify-center brutal-shadow">
              <FileText className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono-tight text-[11px] uppercase tracking-[0.25em] opacity-90 mb-1">
                Required reading
              </div>
              <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight">
                Read the school information pack before booking
              </h3>
              <p className="text-sm md:text-base mt-2 opacity-95">
                Kit, safety, supervision ratios and rink rules for teachers — please review with your team.
              </p>
            </div>
          </div>
          <a
            href={INFO_PACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-slate-900 font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-5 py-3 flex items-center gap-2 whitespace-nowrap"
            data-testid="info-pack-download"
          >
            <Download className="w-4 h-4" strokeWidth={3} /> Download pack
          </a>
        </div>
      </section>

      {/* SLOT GRID */}
      <section id="slot-grid" className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">
              Step 01 — Choose your slot
            </div>
            <h2 className="font-display text-3xl md:text-4xl uppercase text-slate-900">
              Available sessions
            </h2>
          </div>
          <div className="flex items-center gap-4 font-mono-tight text-[11px] uppercase tracking-wider">
            <LegendDot cls="bg-white border-2 border-slate-900" label="Available" />
            <LegendDot cls="bg-slate-900" label="Selected" />
            <LegendDot cls="bg-slate-100 border-2 border-slate-200" label="Booked · click for waitlist" />
          </div>
        </div>

        {loading ? (
          <div className="border-2 border-slate-900 bg-white p-16 text-center font-mono-tight uppercase tracking-widest">
            Loading…
          </div>
        ) : (
          <div className="grid gap-4">
            {visibleAvailability.map((day, idx) => {
              const prev = visibleAvailability[idx - 1];
              const curDate = parseISODate(day.date);
              let showDivider = false;
              if (!prev) {
                showDivider = true;
              } else {
                const prevDate = parseISODate(prev.date);
                const gap = (curDate - prevDate) / (1000 * 60 * 60 * 24);
                const monthChanged =
                  curDate.getMonth() !== prevDate.getMonth() ||
                  curDate.getFullYear() !== prevDate.getFullYear();
                if (gap > 3 || monthChanged) showDivider = true;
              }
              const dividerLabel = showDivider
                ? `${MONTHS[curDate.getMonth()]} ${curDate.getFullYear()}`
                : "";
              return (
                <div key={day.date}>
                  {showDivider && (
                    <div className="flex items-center gap-4 mt-6 mb-2 first:mt-0" data-testid={`month-divider-${day.date}`}>
                      <div className="font-mono-tight text-[11px] uppercase tracking-[0.3em] text-slate-500">{dividerLabel}</div>
                      <div className="flex-1 h-[2px] bg-slate-900" />
                    </div>
                  )}
                  <DateRow
                    day={day}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    onSelect={onSelectSlot}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* BOOKING FORM */}
      {(selectedDate || confirmation) && (
        <section id="booking-form-section" className="border-t-2 border-slate-900 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
            {confirmation ? (
              <div className="border-2 border-slate-900 bg-white brutal-shadow-lg p-8 md:p-10 animate-fade-up" data-testid="confirmation-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-sky-500 border-2 border-slate-900 flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" strokeWidth={3} />
                  </div>
                  <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500">
                    Submitted
                  </div>
                </div>
                <h3 className="font-display text-3xl md:text-4xl uppercase mb-3">Booking submitted</h3>
                <p className="text-sm md:text-base text-slate-700 mb-6" data-testid="confirmation-intro">
                  Thanks! <span className="font-mono-tight font-bold text-slate-900">Clonmel On Ice</span> will be in touch with confirmation via email.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <InfoRow label="Reference">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-tight font-bold text-lg" data-testid="confirmation-reference">
                        {confirmation.reference}
                      </span>
                      <button
                        onClick={copyRef}
                        className="border-2 border-slate-900 bg-white hover:bg-sky-100 p-1"
                        data-testid="copy-reference"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </InfoRow>
                  <InfoRow label="Date & Time">
                    <span className="font-mono-tight font-bold">
                      {formatDateLong(confirmation.date)} · {confirmation.time}
                    </span>
                  </InfoRow>
                  <InfoRow label="School">{confirmation.school_name}</InfoRow>
                  <InfoRow label="School address">{confirmation.school_address}</InfoRow>
                  <InfoRow label="Teacher">{confirmation.teacher_name}</InfoRow>
                  <InfoRow label="Group size">{confirmation.group_size}</InfoRow>
                  <InfoRow label="County">{confirmation.county}</InfoRow>
                  <InfoRow label="Age group">{confirmation.age_group}</InfoRow>
                  <InfoRow label="Preferred payment">{confirmation.payment_method}</InfoRow>
                </div>
                <div className="border-t-2 border-slate-900 pt-6 flex flex-wrap gap-3">
                  <a
                    href="/lookup"
                    className="bg-white text-slate-900 font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3"
                  >
                    Lookup by reference
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="animate-fade-up" data-testid="booking-form">
                <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">
                  Step 02 — Your details
                </div>
                <h2 className="font-display text-3xl md:text-4xl uppercase text-slate-900 mb-2">
                  Confirm your session
                </h2>
                <div className="font-mono-tight text-sm text-slate-700 mb-8 flex items-center gap-2 flex-wrap">
                  <span className="bg-slate-900 text-white px-3 py-1">
                    {formatDateLong(selectedDate)}
                  </span>
                  <span className="bg-sky-500 text-white px-3 py-1">{selectedTime}</span>
                  <span className="text-slate-500">45 minutes</span>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="School name" icon={School} required>
                    <input
                      required
                      value={form.school_name}
                      onChange={change("school_name")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-school-name"
                    />
                  </Field>
                  <Field label="School address" icon={MapPin} required>
                    <input
                      required
                      value={form.school_address}
                      onChange={change("school_address")}
                      placeholder="Street, town, Eircode"
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-school-address"
                    />
                  </Field>
                  <Field label="Teacher name" icon={User} required>
                    <input
                      required
                      value={form.teacher_name}
                      onChange={change("teacher_name")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-teacher-name"
                    />
                  </Field>
                  <Field label="Email" icon={Mail} required>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={change("email")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-email"
                    />
                  </Field>
                  <Field label="Phone" icon={Phone} required>
                    <input
                      required
                      value={form.phone}
                      onChange={change("phone")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-phone"
                    />
                  </Field>
                  <Field label="County" icon={MapPin} required>
                    <select
                      required
                      value={form.county}
                      onChange={change("county")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-county"
                    >
                      <option value="">Select county…</option>
                      {(config?.counties || []).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Preferred payment" icon={CreditCard} required>
                    <select
                      required
                      value={form.payment_method}
                      onChange={change("payment_method")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-payment-method"
                    >
                      <option value="">Select payment…</option>
                      {(config?.payment_methods || []).map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Group size" icon={Users} required>
                    <input
                      required
                      type="number"
                      min="1"
                      max="75"
                      value={form.group_size}
                      onChange={change("group_size")}
                      placeholder="Max 75"
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-group-size"
                    />
                  </Field>
                  <Field label="Age group" icon={CalendarIcon} required>
                    <select
                      required
                      value={form.age_group}
                      onChange={change("age_group")}
                      className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                      data-testid="input-age-group"
                    >
                      <option value="">Select…</option>
                      <option value="Primary - 1st to 3rd Class">Primary - 1st to 3rd Class</option>
                      <option value="Primary - 4th to 6th Class">Primary - 4th to 6th Class</option>
                      <option value="Post Primary - 1st Years to 3rd Years">Post Primary - 1st Years to 3rd Years</option>
                      <option value="Post Primary - 4th Years to 6th Years">Post Primary - 4th Years to 6th Years</option>
                    </select>
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Notes (optional)">
                      <textarea
                        rows={3}
                        value={form.notes}
                        onChange={change("notes")}
                        placeholder="Skill level, accessibility needs, teaching assistants attending…"
                        className="w-full bg-white border-2 border-slate-900 p-3 font-mono-tight text-sm focus:outline-none focus:border-sky-500"
                        data-testid="input-notes"
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="submit-booking"
                  >
                    {submitting ? "Submitting…" : "Submit Request"}
                    {!submitting && <ArrowRight className="w-4 h-4" strokeWidth={3} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDate(null);
                      setSelectedTime(null);
                    }}
                    className="bg-white text-slate-900 font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3"
                    data-testid="cancel-booking"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      )}

      {/* WAITLIST MODAL */}
      <WaitlistModal
        open={!!waitlistSlot}
        date={waitlistSlot?.date}
        time={waitlistSlot?.time}
        onClose={() => setWaitlistSlot(null)}
      />

      {/* FOOTER */}
      <footer className="border-t-2 border-slate-900 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em]">
            School Ice · Rink Booking 2026/27
          </div>
          <div className="font-mono-tight text-xs text-slate-400 uppercase tracking-widest">
            4 × 45 min · Mon–Fri · {config ? rangesLabel(config.booking_ranges) : "30 Nov – 22 Dec"}
          </div>
        </div>
      </footer>
    </div>
  );
}

const StatBlock = ({ label, value }) => (
  <div>
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
    <div className="font-display text-2xl leading-none">{value}</div>
  </div>
);

const LegendDot = ({ cls, label, strike }) => (
  <div className="flex items-center gap-2">
    <span className={`inline-block w-4 h-4 ${cls}`} />
    <span className={`text-slate-700 ${strike ? "line-through text-slate-400" : ""}`}>{label}</span>
  </div>
);

const Field = ({ label, icon: Icon, required, children }) => (
  <label className="block">
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.5} />}
      <span className="font-mono-tight text-[11px] uppercase tracking-[0.2em] text-slate-500">
        {label}
        {required && <span className="text-sky-500 ml-1">*</span>}
      </span>
    </div>
    {children}
  </label>
);

const InfoRow = ({ label, children }) => (
  <div>
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
    <div className="text-slate-900">{children}</div>
  </div>
);

const DateRow = ({ day, selectedDate, selectedTime, onSelect }) => {
  const d = parseISODate(day.date);
  const allBooked = day.slots.every((s) => s.booked);
  return (
    <div
      className={`border-2 border-slate-900 bg-white grid md:grid-cols-[220px_1fr] ${
        allBooked ? "opacity-70" : ""
      }`}
      data-testid={`date-row-${day.date}`}
    >
      <div className="border-b-2 md:border-b-0 md:border-r-2 border-slate-900 p-5 flex items-center justify-between md:justify-start md:gap-5 bg-slate-50">
        <div>
          <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {day.weekday}
          </div>
          <div className="font-display text-3xl md:text-4xl uppercase leading-none mt-1">
            {formatDateShort(day.date)}
          </div>
        </div>
        {allBooked && (
          <div className="font-mono-tight text-[10px] uppercase tracking-widest bg-slate-900 text-white px-2 py-1">
            Full
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {day.slots.map((slot) => {
          const isSelected = selectedDate === day.date && selectedTime === slot.time;
          const base =
            "p-4 md:p-5 border-r-2 last:border-r-0 border-b-2 md:border-b-0 border-slate-900 font-mono-tight font-bold text-center transition-colors";
          let cls = "";
          if (slot.blocked) {
            cls = "bg-slate-900 text-white cursor-not-allowed";
          } else if (slot.booked) {
            cls = "bg-slate-100 text-slate-500 cursor-pointer hover:bg-slate-200";
          } else if (isSelected) {
            cls = "bg-slate-900 text-white cursor-pointer";
          } else {
            cls = "bg-white text-slate-900 hover:bg-sky-100 cursor-pointer";
          }
          return (
            <button
              key={slot.time}
              type="button"
              onClick={() => onSelect(day.date, slot.time, slot.booked, slot.blocked)}
              disabled={slot.blocked}
              className={`${base} ${cls}`}
              data-testid={`slot-${day.date}-${slot.time.replace(":", "")}`}
            >
              <div className={`text-lg ${slot.booked && !slot.blocked ? "line-through decoration-slate-400" : ""}`}>{slot.time}</div>
              <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80 flex items-center justify-center gap-1">
                {slot.blocked ? (
                  "Closed"
                ) : slot.booked ? (
                  <>
                    <Bell className="w-3 h-3" strokeWidth={3} /> Waitlist
                  </>
                ) : isSelected ? "Selected" : "45 min"}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
